import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from './useDebounce';

export function useEditableEmailValidator({
  fieldName,
  cardId,
  emailDomains = [],
  delay = 300,
}) {
  const [error, setError] = useState('');
  const [info, setInfo] = useState([]);
  const suggestionRef = useRef(null);
  const targetRef = useRef(null);

  const basicRegex = useRef(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@.+\.[^<>()[\]\\.,;:\s@"]{2,}$/i
  ).current;

  const sanitize = useCallback((s) => s.replace(/\s+/g, '').trim(), []);
  const validate = useCallback(
    (value) => {
      suggestionRef.current = null;
      if (!value) return '';

      const [, domain = ''] = value.split('@');
      const lower = domain.toLowerCase();

      if (domain) {
        const matches = emailDomains.filter((d) => d.startsWith(lower)).sort();
        const suggestion = matches[0];
        if (suggestion && suggestion !== lower) {
          suggestionRef.current = suggestion;
          return I18n.t('email_validator_alert.email_suggestion', {
            domain: `@${suggestion}`,
          });
        }
        if (emailDomains.includes(lower)) {
          return '';
        }

        return I18n.t('email_validator_alert.unknown_email_domain', {
          email_domain: lower,
        });
      }

      return basicRegex.test(value)
        ? ''
        : I18n.t('email_validator_alert.email_invalid');
    },
    [emailDomains, basicRegex]
  );

  const debouncedValidate = useDebounce(
    (txt) => setError(validate(txt)),
    delay
  );

  const normalize = useCallback(
    (raw) => {
      const cleaned = sanitize(raw);
      const lowercased = cleaned.toLowerCase();
      const msgs = [];
      if (cleaned !== raw)
        msgs.push(I18n.t('email_validator_alert.spaces_removed'));
      if (lowercased !== cleaned)
        msgs.push(I18n.t('email_validator_alert.text_lowercased'));
      setInfo(msgs);
      return lowercased;
    },
    [sanitize]
  );

  const setCaretToEnd = useCallback((el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }, []);

  useEffect(() => {
    const selector = cardId
      ? `div[data-scrivito-field-name="${fieldName}"][data-card-id="${cardId}"]`
      : `div[data-scrivito-field-name="${fieldName}"]`;
    const el = document.querySelector(selector);
    if (!el) return;
    targetRef.current = el;

    el.setAttribute('role', 'textbox');
    el.setAttribute('aria-invalid', 'false');

    const update = (raw, focus = false) => {
      const norm = normalize(raw);
      el.innerText = norm;
      if (focus) setCaretToEnd(el);

      const err = validate(norm);
      setError(err);
    };

    const onInput = () => debouncedValidate(el.innerText);
    const onPaste = (e) => {
      e.preventDefault();
      update((e.clipboardData || window.clipboardData).getData('text'), true);
    };
    const onBlur = () => update(el.innerText, false);
    const onKeyDown = (e) => {
      if (e.key === 'Tab' && suggestionRef.current) {
        e.preventDefault();
        const [user] = el.innerText.split('@');
        update(`${user}@${suggestionRef.current}`, true);
      }
    };

    el.addEventListener('input', onInput);
    el.addEventListener('paste', onPaste);
    el.addEventListener('blur', onBlur);
    el.addEventListener('keydown', onKeyDown);

    debouncedValidate(el.innerText);

    return () => {
      el.removeEventListener('input', onInput);
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('blur', onBlur);
      el.removeEventListener('keydown', onKeyDown);
    };
  }, [
    fieldName,
    cardId,
    normalize,
    validate,
    setCaretToEnd,
    debouncedValidate,
  ]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;

    el.setAttribute('aria-invalid', (!!error).toString());

    const desc = [];
    if (info.length) desc.push(`email-info-${cardId}`);
    if (error) desc.push(`email-error-${cardId}`);
    if (desc.length) el.setAttribute('aria-describedby', desc.join(' '));
    else el.removeAttribute('aria-describedby');

    if (info.length) {
      const t = setTimeout(() => setInfo([]), 3000);
      return () => clearTimeout(t);
    }
  }, [error, info, cardId]);

  return { error, info };
}
